/**
 * UPT-2079 L14a (web §5.2, §6) — vignette d'une ressource : URL `data:` construite depuis les octets SERVIS (ré-encodés par le serveur), après vérification de la
 * signature PNG ou JPEG. Jamais `URL.createObjectURL` (garde `gate:analysis`) ; jamais un type pris sur la parole d'un en-tête. Fonctions pures.
 */

export type SniffedImage = { mediaType: 'image/png' | 'image/jpeg'; width: number; height: number };

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];

const u32 = (bytes: Uint8Array, offset: number): number =>
  ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0;

const u16 = (bytes: Uint8Array, offset: number): number => (bytes[offset] << 8) | bytes[offset + 1];

/** Vrai si les octets commencent par la signature PNG. */
export const isPng = (bytes: Uint8Array): boolean => bytes.length >= 8 && PNG_SIGNATURE.every((value, index) => bytes[index] === value);

/** Vrai si les octets commencent par la signature JPEG (`FF D8 FF`). */
export const isJpeg = (bytes: Uint8Array): boolean => bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;

/**
 * Type et dimensions lus dans l'EN-TÊTE (PNG `IHDR`, premier `SOFn` d'un JPEG), sans décodage ; `null` si la signature n'est ni PNG ni JPEG ou si l'en-tête est
 * tronqué (comme le contrôle du serveur, rest §4.8 : WebP, GIF et SVG ne passent pas).
 */
export const sniffImage = (bytes: Uint8Array): SniffedImage | null => {
  if (isPng(bytes)) {
    if (bytes.length < 24 || u32(bytes, 12) !== 0x49484452) return null;
    return { mediaType: 'image/png', width: u32(bytes, 16), height: u32(bytes, 20) };
  }
  if (isJpeg(bytes)) {
    let offset = 2;
    while (offset + 4 <= bytes.length) {
      if (bytes[offset] !== 0xff) return null;
      const marker = bytes[offset + 1];
      if (marker === 0xff) {
        offset += 1;
        continue;
      }
      if (marker === 0xd8 || (marker >= 0xd0 && marker <= 0xd7) || marker === 0x01) {
        offset += 2;
        continue;
      }
      const length = u16(bytes, offset + 2);
      if (length < 2) return null;
      const isSof = marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc;
      if (isSof) {
        if (offset + 9 > bytes.length) return null;
        return { mediaType: 'image/jpeg', height: u16(bytes, offset + 5), width: u16(bytes, offset + 7) };
      }
      offset += 2 + length;
    }
    return null;
  }
  return null;
};

/** Base64 par morceaux (pas de dépassement de pile sur une image de 500 Kio). */
export const bytesToBase64 = (bytes: Uint8Array): string => {
  let binary = '';
  const chunk = 0x8000;
  for (let index = 0; index < bytes.length; index += chunk) {
    binary += String.fromCharCode(...Array.from(bytes.subarray(index, index + chunk)));
  }
  return btoa(binary);
};

/** URL `data:` de la vignette, ou `null` si la signature n'est ni PNG ni JPEG (l'image n'est alors pas montrée). */
export const thumbDataUrl = (buffer: ArrayBuffer | Uint8Array): string | null => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const sniffed = sniffImage(bytes);
  if (!sniffed) return null;
  return `data:${sniffed.mediaType};base64,${bytesToBase64(bytes)}`;
};
